// Componenti cella riusabili per le tabelle (DataGrid) — standard unico app.
//
// Usano le classi globali definite in `src/app/styles/forms.css`
// (`hist-when`, `hist-route`, `day-pill`, `cell-strong/muted`, `row-actions`,
// `tbl-*`). Pensati per costruire colonne coerenti in QUALSIASI tabella, non
// solo nello Storico utente. Import via barrel `@ui`.

import { Tooltip } from '@mui/material';
import i18n from 'i18next';

import { faClass } from '@/constants/icons';
import { getRoutePoints } from '@/constants/orderRoute';

import AppStatusChip from '../buttons/AppStatusChip';
// Import diretto e non via `@ui`: il barrel ri-esporta questo stesso file, e
// passare da lì chiude un ciclo (import/no-cycle).
import Icon from '../icon/Icon';
/**
 * Cella "Quando": riga principale (data/icona) + contenuto secondario (orario)
 */
export function WhenCell({ icon, primary, secondary, children, faded = false }) {
  return (
    <div className="hist-when" style={faded ? { opacity: 0.55 } : undefined}>
      <span className="hist-date">
        {icon ? (
          <i className={faClass(icon)} aria-hidden="true" style={{ marginRight: 6 }} />
        ) : null}
        {primary || '—'}
      </span>
      {secondary && <span className="time-badge">{secondary}</span>}
      {children}
    </div>
  );
}

/**
 * Cella Passeggero (Identità): avatar rotondo scuro + nome blu sottolineato.
 */
export function PassengerCell({ name, icon = 'fa-user' }) {
  return (
    <div className="cell-id">
      <div className="avatar-circle">
        <i className={faClass(icon)} />
      </div>
      <span className="passenger-link">{name || '—'}</span>
    </div>
  );
}

/**
 * Cella Autista: avatar circolare grigio + nome scuro + riga secondaria (mezzo / gestore).
 */
export function OperativeCell({ name, vehicle, manager, icon = 'fa-id-card-clip' }) {
  return (
    <div className="cell-id">
      <div className="avatar-circle avatar-circle--muted">
        <i className={faClass(icon)} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <span
          className="cell-strong"
          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {name || '—'}
        </span>
        {(vehicle || manager) && (
          <span
            className="cell-muted"
            style={{
              fontSize: '11px',
              marginTop: '-2px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {vehicle ? vehicle : ''}
            {vehicle && manager ? ' • ' : ''}
            {manager ? manager : ''}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Pillole di stato con pallino colorato (es: P1, P2 o Completato)
 * items = array di oggetti { label, color }
 */
export function StatusPills({ items }) {
  const list = Array.isArray(items) ? items.filter((x) => x != null) : [];
  if (!list.length) return <span className="cell-muted">—</span>;
  return (
    <div className="status-pills">
      {list.map((item, i) => (
        <AppStatusChip
          key={`${item.label}-${i}`}
          variant={item.color || 'neutral'}
          dot
          label={item.label}
          fullCell={false}
        />
      ))}
    </div>
  );
}

/**
 * Cella Prezzo. Formatta importi con due decimali. Supporta costi extra (es. fermo auto).
 */
export function PriceCell({ amount, extraCost = 0, currency = '€' }) {
  if (amount == null) return <span className="cell-muted">—</span>;
  const num = typeof amount === 'number' ? amount : parseFloat(amount);
  if (isNaN(num)) return <span className="cell-muted">—</span>;

  const totale = num + extraCost;

  if (extraCost <= 0) {
    return (
      <span className="price-cell">
        {totale.toFixed(2)} {currency}
      </span>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <span className="price-cell" title={`${totale.toFixed(2)} ${currency}`}>
        {totale.toFixed(2)} {currency}
      </span>
      <span
        className="cell-muted"
        style={{ fontSize: '10px', marginTop: '-4px' }}
        title={`${extraCost.toFixed(2)}${currency} ${i18n.t('fermo_auto_incluso')}`}
      >
        ({extraCost.toFixed(2)} {currency} {i18n.t('fermo_auto_incluso')})
      </span>
    </div>
  );
}

/** Pillole (giorni di ricorrenza o tag brevi). `items` = array di stringhe. */
export function DayPills({ items }) {
  const list = Array.isArray(items) ? items.filter((x) => x != null && x !== '') : [];
  if (!list.length) return <span className="cell-muted">—</span>;
  return (
    <div className="day-pills">
      {list.map((label, i) => (
        <span className="day-pill" key={`${label}-${i}`}>
          {label}
        </span>
      ))}
    </div>
  );
}

/**
 * Tratta dell'ordine, con la forma della sua modalità di servizio: due righe
 * partenza→arrivo per il trasporto, due stazioni per il treno, UNA sola riga
 * con pin per un servizio a punto singolo (assistenza) — niente riga di arrivo
 * vuota con il trattino penzolante. Ogni riga tronca con ellissi + tooltip.
 *
 * Passare `order` (la riga della griglia) per il comportamento per-modalità.
 * Le prop `from`/`to` restano supportate per le tabelle che pre-mappano le
 * righe su una forma propria (servizi azienda, viaggi giornalieri).
 */
export function RouteCell({ order, from, to, faded = false }) {
  const points = order
    ? getRoutePoints(order).points
    : [
        { address: from, icon: 'circleDot' },
        { address: to, icon: 'destination' },
      ];

  return (
    <div className="hist-route" style={faded ? { opacity: 0.55 } : undefined}>
      {points.map((point, i) => (
        <div
          key={point.icon + i}
          className={`hist-route__line hist-route__line--${i === 0 ? 'from' : 'to'}`}
          title={point.address || ''}
        >
          <Icon name={point.icon} aria-hidden="true" />
          <span className="hist-route__text">{point.address || '—'}</span>
        </div>
      ))}
    </div>
  );
}

/** Chip di stato attivo/inattivo. */
export function StatePill({ active, activeLabel = 'Attivo', inactiveLabel = 'Inattivo' }) {
  return (
    <AppStatusChip
      variant={active ? 'success' : 'neutral'}
      dot
      label={active ? activeLabel : inactiveLabel}
      fullCell={false}
    />
  );
}

/** Contenitore azioni di riga (bottoni icona allineati a destra). */
export function RowActions({ children }) {
  return <div className="row-actions">{children}</div>;
}
/**
 * Affordance di espansione per le righe-gruppo (group_id): chevron + conteggio.
 * Da usare nella cella leading (gutter slim) delle righe `__kind === 'group'`.
 * Per le righe figlie usa `<ChildMarker />`.
 */
export function GroupExpander({ expanded, count, onToggle, label = 'Gruppo' }) {
  return (
    <button
      type="button"
      className="tbl-expander"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-label={`${label}${count != null ? ` (${count})` : ''}`}
      title={`${label}${count != null ? ` (${count} viaggi)` : ''} — ${
        expanded ? 'comprimi' : 'espandi'
      }`}
    >
      <i className={`fa-solid fa-chevron-${expanded ? 'down' : 'right'}`} aria-hidden="true" />
      {count != null ? <span className="tbl-expander__count">{count}</span> : null}
    </button>
  );
}

/** Marcatore di riga figlia (indent) nelle tabelle con righe-gruppo. */
export function ChildMarker() {
  return (
    <span className="tbl-child-marker" aria-hidden="true">
      <Icon name="turnUp" className="fa-rotate-90" />
    </span>
  );
}

/**
 * Cella "Dettagli": le condizioni operative di un viaggio in icone —
 * carrozzina, allettati, accompagnatori, secondo operatore, note.
 *
 * Sono i dati che decidono *come* si esegue il servizio (che mezzo serve,
 * quanti posti, chi accompagna) e che altrimenti si vedrebbero solo aprendo il
 * viaggio: in una griglia di pianificazione devono stare sulla riga.
 *
 * Ogni valore è opzionale e ciò che manca semplicemente non si disegna. I
 * conteggi passeggeri vanno passati solo dove ha senso trasportare persone:
 * `showPassengers={false}` li nasconde (trasporto oggetti, assistenza).
 *
 * @param {Object} props
 * @param {number} [props.wheelchair] passeggeri in carrozzina
 * @param {number} [props.bedridden] passeggeri allettati
 * @param {number} [props.companions] accompagnatori
 * @param {string} [props.note]
 * @param {string} [props.secondOperatorName] nome del secondo operatore, se previsto
 * @param {boolean} [props.showPassengers] default true
 * @param {React.ReactNode} [props.children] icone aggiuntive di dominio
 */
export function TripDetailsCell({
  wheelchair = 0,
  bedridden = 0,
  companions = 0,
  note = '',
  secondOperatorName = '',
  showPassengers = true,
  children = null,
}) {
  const hasWheelchair = showPassengers && Number(wheelchair) > 0;
  const hasBedridden = showPassengers && Number(bedridden) > 0;
  const hasCompanions = showPassengers && Number(companions) > 0;
  const text = typeof note === 'string' ? note.trim() : '';
  const isEmpty =
    !hasWheelchair && !hasBedridden && !hasCompanions && !secondOperatorName && !text && !children;

  // Una cella vuota resta leggibile come "nessun dettaglio", non come dato
  // mancante: in una colonna di sole icone il vuoto è ambiguo.
  if (isEmpty) return <span className="tbl-details__empty">—</span>;

  return (
    <div className="tbl-details">
      {hasWheelchair && (
        <span className="tbl-details__item tbl-details__item--alert" title={i18n.t('carrozzina')}>
          <Icon name="wheelchair" />
          {Number(wheelchair) > 1 ? Number(wheelchair) : null}
        </span>
      )}
      {hasBedridden && (
        <span className="tbl-details__item tbl-details__item--alert" title={i18n.t('allettati')}>
          <Icon name="ambulance" />
          {Number(bedridden) > 1 ? Number(bedridden) : null}
        </span>
      )}
      {hasCompanions && (
        <span className="tbl-details__item" title={i18n.t('accompagnatori')}>
          <Icon name="user" size="sm" /> {Number(companions)}
        </span>
      )}
      {secondOperatorName && (
        <span className="tbl-details__item" title={secondOperatorName}>
          <Icon name="peopleCarry" />
        </span>
      )}
      {children}
      {text && (
        <Tooltip
          arrow
          placement="top"
          slotProps={{ tooltip: { sx: { bgcolor: 'transparent', p: 0, maxWidth: 'none' } } }}
          title={
            <div className="tbl-tooltip-card">
              <div className="tbl-tooltip-card__title">
                <Icon name="note" /> {i18n.t('note', { defaultValue: 'Note' })}
              </div>
              <div className="tbl-tooltip-card__text">{text}</div>
            </div>
          }
        >
          <span className="tbl-details__item tbl-details__note">
            <Icon name="info" />
          </span>
        </Tooltip>
      )}
    </div>
  );
}
